import { API_BASE, apiFetch } from "./apiBase";

async function readApiErrorMessage(response: Response, fallbackMessage: string): Promise<string> {
  const body = await response.text().catch(() => "");
  if (!body) {
    return fallbackMessage;
  }
  try {
    const parsed = JSON.parse(body) as { error?: unknown };
    if (typeof parsed.error === "string" && parsed.error.trim()) {
      return parsed.error.trim();
    }
  } catch {
    // plain text or HTML error page
  }
  return body.length <= 240 ? body : fallbackMessage;
}

async function expectOk(response: Response, fallbackMessage: string): Promise<void> {
  if (response.ok) {
    return;
  }
  throw new Error(await readApiErrorMessage(response, fallbackMessage));
}

export interface AuthSessionPayload {
  authenticated: boolean;
  /**
   * The user's home account id. Sessions are identity-only; this is the anchor
   * used for "create new team", profile lookups, etc. Cross-account access
   * (teams/projects shared with the user) is surfaced via the aggregated hub
   * endpoint at /api/auth/my-domain, where each item carries its real accountId.
   */
  accountId?: string;
  /** Explicit alias for `accountId`. */
  homeAccountId?: string;
  email?: string;
  provider?: "google" | "email";
  isAdmin?: boolean;
  /** True when session email is an exact entry in API `ADMIN_EMAILS`. */
  adminAccessAllowed?: boolean;
  adminEmailListed?: boolean;
  /** True when session email is listed in API `QA_EMAILS` (comma-separated). */
  isQa?: boolean;
  fullName?: string | null;
  avatarUrl?: string | null;
  avatarLocked?: boolean;
  profileComplete?: boolean;
  /**
   * Present (with `active: true`) when the current session is an admin
   * impersonating another user. `isAdmin`/`isQa` are forced to false on the
   * server while this is active so the UI fully takes on the target user's
   * experience. The bottom-left banner is the only exit affordance.
   */
  impersonation?: {
    active: true;
    targetName: string | null;
    targetEmail: string;
    byEmail: string;
  };
}

/** Exact value required for CORS “simple request” (no preflight). Do not append charset. */
const formUrlEncoded = "application/x-www-form-urlencoded";

export async function startGoogleAuth(): Promise<{ authorizationUrl: string }> {
  // Form body avoids CORS preflight (some hosts mishandle OPTIONS to Node).
  const response = await apiFetch(`${API_BASE}/auth/google/start`, {
    method: "POST",
    headers: { "Content-Type": formUrlEncoded },
    body: new URLSearchParams(),
  });
  await expectOk(response, "Unable to start Google authentication.");
  return (await response.json()) as { authorizationUrl: string };
}

export type RateLimitTier = "5min" | "1hr" | "1day" | "banned";

export class RateLimitError extends Error {
  readonly tier: RateLimitTier;
  readonly retryAfter?: number; // ms epoch

  constructor(tier: RateLimitTier, retryAfter?: number) {
    super(`rate_limited:${tier}`);
    this.tier = tier;
    this.retryAfter = retryAfter;
  }
}

export async function startEmailMagicLink(email: string): Promise<{ ok: boolean; devLink?: string | null }> {
  const response = await apiFetch(`${API_BASE}/auth/email/start`, {
    method: "POST",
    headers: { "Content-Type": formUrlEncoded },
    body: new URLSearchParams({ email }),
  });
  if (response.status === 429) {
    const body = await response.json().catch(() => ({})) as { error?: string; tier?: string; retryAfter?: number };
    if (body.error === "rate_limited") {
      throw new RateLimitError((body.tier ?? "5min") as RateLimitTier, body.retryAfter);
    }
    throw new Error("Too many attempts. Please try again later.");
  }
  await expectOk(response, "Unable to start email sign-in.");
  return (await response.json()) as { ok: boolean; devLink?: string | null };
}

export async function fetchAuthSession(): Promise<AuthSessionPayload> {
  const response = await apiFetch(`${API_BASE}/auth/session`);
  if (response.status === 401) {
    return { authenticated: false };
  }
  await expectOk(response, "Unable to fetch auth session.");
  const payload = (await response.json()) as AuthSessionPayload;
  if (!payload.authenticated) {
    return { authenticated: false };
  }
  return payload;
}

export async function logoutAuthSession(): Promise<void> {
  const response = await apiFetch(`${API_BASE}/auth/logout`, {
    method: "POST",
    headers: { "Content-Type": formUrlEncoded },
    body: new URLSearchParams(),
  });
  await expectOk(response, "Unable to logout.");
}

export interface AuthProfilePayload {
  profile: {
    fullName: string | null;
    avatarUrl: string | null;
    avatarLocked: boolean;
    showcasePromoConsent?: boolean;
    showcasePromoConsentAt?: string | null;
    showcaseCompanyConsent?: boolean;
    showcaseDesignsConsent?: boolean;
  };
}

export type AccountOwnershipSummary = {
  ownsTeamsOrProjects: boolean;
  teams: Array<{ accountId: string; teamCode: string; teamName: string }>;
  projects: Array<{ accountId: string; projectCode: string; projectName: string; teamCode: string }>;
};

export async function fetchAccountOwnershipSummary(): Promise<AccountOwnershipSummary> {
  const response = await apiFetch(`${API_BASE}/auth/account/ownership-summary`);
  await expectOk(response, "Unable to load account ownership summary.");
  return (await response.json()) as AccountOwnershipSummary;
}

export async function fetchAuthProfile(): Promise<AuthProfilePayload> {
  const response = await apiFetch(`${API_BASE}/auth/profile`);
  await expectOk(response, "Unable to fetch profile.");
  return (await response.json()) as AuthProfilePayload;
}

export async function saveAuthProfile(payload: {
  fullName?: string;
  avatarUrl?: string | null;
  showcasePromoConsent?: boolean;
  showcaseCompanyConsent?: boolean;
  showcaseDesignsConsent?: boolean;
}): Promise<AuthProfilePayload> {
  const response = await apiFetch(`${API_BASE}/auth/profile`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  await expectOk(response, "Unable to save profile.");
  return (await response.json()) as AuthProfilePayload;
}

export async function uploadProfileAvatar(file: Blob): Promise<{ avatarUrl: string }> {
  const body = new FormData();
  body.append("avatar", file, "avatar.png");
  const response = await apiFetch(`${API_BASE}/auth/profile/avatar`, {
    method: "POST",
    body,
  });
  await expectOk(response, "Unable to upload avatar.");
  return (await response.json()) as { avatarUrl: string };
}

export async function changeAccountEmail(email: string): Promise<{ ok: boolean; email?: string; conflict?: boolean }> {
  const response = await apiFetch(`${API_BASE}/auth/account/email`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(body || "Unable to change email.");
  }
  return (await response.json()) as { ok: boolean; email?: string; conflict?: boolean };
}

export async function deleteAuthAccount(transferOwnershipTo?: string): Promise<void> {
  const response = await apiFetch(`${API_BASE}/auth/account`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(transferOwnershipTo ? { transferOwnershipTo } : {}),
  });
  await expectOk(response, "Unable to delete account.");
}

/** Admin-only: begin impersonating a user. The server swaps the session cookie. */
export async function startImpersonation(params: { targetAccountId?: string; targetEmail?: string }): Promise<void> {
  const response = await apiFetch(`${API_BASE}/admin/impersonate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  await expectOk(response, "Unable to start impersonation.");
}

/** Exit impersonation — swaps the cookie back to the admin's original session. */
export async function exitImpersonation(): Promise<void> {
  const response = await apiFetch(`${API_BASE}/admin/impersonate/exit`, {
    method: "POST",
  });
  await expectOk(response, "Unable to exit impersonation.");
}
